import { useRef } from 'react';
import PropTypes from 'prop-types';

import { useSearchParams } from 'react-router';

import { connect } from 'react-redux';

import { Text, theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { ProgressTracker } from 'Components/ProgressTracker';
import { Steps } from 'Components/Steps';
import Title from 'Components/Title';
import { Typography } from 'Components/Typography';
import { ConsultationFooter } from '../Components/ConsultationFooter';
import { ConsultationTip } from '../Components/ConsultationTip';

import BlockLayout from 'Blocks/BlockLayout';

import consultationCategoryProp from 'PropTypes/consultationProps';
import consultationSubsetCategoryProp from 'PropTypes/consultationSubsetProps';
import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useHeight from 'utils/useHeight';

import * as consultationSelectors from 'dux/consultation/selectors';

import BaseScene from './BaseScene';

const InstructionsTypography = styled(Typography)`
  max-width: 915px;
`;

/* Shameful hack but saves lots of time */
const BoxL = styled(BlockLayout)`
  & > div {
    padding: ${legacyTheme.spacing.s36} 0;
    margin: 0;
  }
  ${legacyTheme.breakpoints.up('sm')} {
    display: flex;
    flex-flow: column;
    align-items: center;
    width: 100%;
    & > div {
      max-width: inherit;
    }
  }
`;

const IconContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;

const StyledBody = styled(Typography)`
  max-width: 332px;
`;

const Separator = styled.span`
  /* reset */
  border: none;

  border-top: 1px solid ${theme.colors.accent[200]};
  margin: ${theme.spacing['4x']} 0;
  width: 48px;
`;

export const QuestionLayout = ({
  body,
  buttonLabel,
  children,
  consultationCategory,
  consultationSubsetCategory,
  disabled,
  display,
  isSaving,
  tipContent,
  progressTrackerIndex,
  questionIndex,
  showProgressTracker,
  submit,
  subtitle,
  title,
  descriptionImage,
  guideText,
  icon,
  showHeaderBackground,
  usePadding,
  addFooter,
  fixedCTA,
  justifyContent,
  hideFooter,
  showSteps,
  questionSet,
}) => {
  const consultationFooterRef = useRef();
  const consultationFooterHeight = useHeight(consultationFooterRef);
  const [params] = useSearchParams();

  const trackerIndex = progressTrackerIndex || questionIndex;

  return (
    <BaseScene
      display={display}
      fixedCTA={fixedCTA}
      fixedTipAndCTA={Boolean(tipContent)}
      footer={addFooter}
      justifyContent={justifyContent}
      padding={usePadding}
    >
      {showProgressTracker && (
        <ProgressTracker
          consultationCategory={consultationCategory}
          consultationSubsetCategory={consultationSubsetCategory}
          questionIndex={trackerIndex}
          routeParams={params}
        />
      )}
      {showSteps && <Steps numberLabels questionIndex={trackerIndex} questions={questionSet} />}
      {showHeaderBackground && title && (
        <BoxL backgroundColor="veryLightVert" contentAlign="center">
          <Title forceLarge theme="white">
            {title}
          </Title>
          {/* We should show subtitle if this precise case if any */}
          {subtitle && (
            <InstructionsTypography align="center" color="white" variant="p2">
              {subtitle}
            </InstructionsTypography>
          )}
        </BoxL>
      )}

      {descriptionImage && (
        <Image
          alt={descriptionImage.alt}
          height={descriptionImage.height}
          src={descriptionImage.source}
          width={descriptionImage.width}
        />
      )}

      {!showHeaderBackground && title && (
        <>
          <Title dataTestid="consultation-question-title" forceLarge>
            {title}
          </Title>
          {subtitle && (
            <InstructionsTypography
              align="center"
              color="grey"
              data-testid="tip-instructions"
              variant="p2"
            >
              {subtitle}
            </InstructionsTypography>
          )}
          {icon && <IconContainer>{icon}</IconContainer>}
        </>
      )}

      {guideText && (
        <>
          <Separator color={theme.colors.accent[200]} width={48} />
          <Text color="neutral.800" fontWeight="medium" variant="bodyLg">
            {guideText}
          </Text>
        </>
      )}

      {children}
      {body && (
        <StyledBody align="center" color="grey" variant="p2">
          {body}
        </StyledBody>
      )}
      {Boolean(tipContent) && (
        <ConsultationTip
          bottomoffset={consultationFooterHeight}
          consultationCategory={consultationCategory}
          tip={tipContent}
        />
      )}
      {!hideFooter && (
        <ConsultationFooter
          ref={consultationFooterRef}
          buttonLabel={buttonLabel}
          disabled={disabled}
          isSaving={isSaving}
          submit={submit}
        />
      )}
    </BaseScene>
  );
};

QuestionLayout.defaultProps = {
  body: null,
  buttonLabel: 'Next',
  consultationSubsetCategory: null,
  disabled: false,
  display: 'flex',
  icon: null,
  isSaving: false,
  hideFooter: false,
  tipContent: null,
  showHeaderBackground: false,
  progressTrackerIndex: null,
  showProgressTracker: true,
  showSteps: false,
  questionIndex: null,
  subtitle: null,
  title: null,
  usePadding: true,
  addFooter: false,
  questionSet: null,
  fixedCTA: true,
  justifyContent: null,
  descriptionImage: null,
  guideText: null,
};

QuestionLayout.propTypes = {
  body: PropTypes.node,
  hideFooter: PropTypes.bool,
  buttonLabel: PropTypes.string,
  children: PropTypes.node.isRequired,
  consultationCategory: consultationCategoryProp.isRequired,
  consultationSubsetCategory: consultationSubsetCategoryProp,
  icon: PropTypes.node,
  disabled: PropTypes.bool,
  display: PropTypes.oneOf(['flex', 'block']),
  isSaving: PropTypes.bool,
  tipContent: PropTypes.shape({}),
  progressTrackerIndex: PropTypes.number,
  questionIndex: PropTypes.number,
  showProgressTracker: PropTypes.bool,
  showSteps: PropTypes.bool,
  showHeaderBackground: PropTypes.bool,
  submit: PropTypes.func.isRequired,
  subtitle: PropTypes.node,
  title: PropTypes.string,
  usePadding: PropTypes.bool,
  addFooter: PropTypes.bool,
  questionSet: PropTypes.arrayOf(PropTypes.shape({})),
  fixedCTA: PropTypes.bool,
  justifyContent: PropTypes.oneOf(['center', 'inherit', 'normal', 'space-between', 'start']),
  descriptionImage: PropTypes.shape({
    alt: PropTypes.string,
    source: srcOrNextImageProp,
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  guideText: PropTypes.string,
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    tipContent: consultationSelectors.getTipForQuestion(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  })
)(QuestionLayout);
